import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialState = {
  addressList: [],
  selectedAddressId: "",
  countryList: [],
  stateList: [],
  loading: false,
  error: null,
  currentAddress: null,
  isEditing: false,
};

const addressSlice = createSlice({
  name: "address",
  initialState,
  reducers: {
    setAddressList: (state, action) => {
      state.addressList = action.payload;
    },
    setSelectedAddress: (state, action) => {
      state.selectedAddressId = action.payload;
    },
    setCountryList: (state, action) => {
      // console.log(action.payload);
      state.countryList = action.payload;
    },
    setStateList: (state, action) => {
      state.stateList = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setCurrentAddress: (state, action) => {
      // const address = state.addressList.find(addr => addr._id === action.payload);
      // state.currentAddress = address;
      // // Pre-populate the form values
      // state.formValues = {
      //     user_id: address?.user_id,
      //     billingAddress: { ...address?.billingAddress },
      //     Ship_different_address: address?.Ship_different_address,
      //     shippingAddress: { ...address?.shippingAddress }
      // };
      // state.currentAddress = action.payload;
      const address = state.addressList.find(
        (addr) => addr._id === action.payload
      );

      // Convert country values to match the format expected by the form
      if (address) {
        // For billing address
        const billingCountry = state.countryList.find(
          (c) =>
            c.name.toLowerCase() === address.billingAddress.country ||
            c.id === address.billingAddress.country
        );

        // For shipping address
        const shippingCountry = state.countryList.find(
          (c) =>
            c.name.toLowerCase() === address.shippingAddress.country ||
            c.id === address.shippingAddress.country
        );

        state.currentAddress = {
          ...address,
          billingAddress: {
            ...address.billingAddress,
            country: billingCountry
              ? billingCountry.id
              : address.billingAddress.country,
          },
          shippingAddress: {
            ...address.shippingAddress,
            country: shippingCountry
              ? shippingCountry.id
              : address.shippingAddress.country,
          },
        };
      }
    },
    setIsEditing: (state, action) => {
      state.isEditing = action.payload;
    },
    resetAddressForm: (state) => {
      state.currentAddress = null;
      state.isEditing = false;
    },
    addGuestAddress: (state, action) => {
      const addresses = [...state.addressList, action.payload];
      state.addressList = addresses;
      // If this is the first address, set it as selected
      if (addresses.length === 1) {
        state.selectedAddressId = action.payload._id;
      }
      // Store in localStorage
      localStorage.setItem("guestAddresses", JSON.stringify(addresses));
    },
    updateGuestAddress: (state, action) => {
      const addresses = state.addressList.map(addr => 
        addr._id === action.payload._id ? action.payload : addr
      );
      state.addressList = addresses;
      localStorage.setItem("guestAddresses", JSON.stringify(addresses));
    },
    deleteGuestAddress: (state, action) => {
      const addresses = state.addressList.filter(addr => 
        addr._id !== action.payload
      );
      state.addressList = addresses;
      localStorage.setItem("guestAddresses", JSON.stringify(addresses));
      // If deleted address was selected, select another one if available
      if (state.selectedAddressId === action.payload && addresses.length > 0) {
        state.selectedAddressId = addresses[0]._id;
      }
    },
  },
});

export const {
  setAddressList,
  setSelectedAddress,
  setCountryList,
  setStateList,
  setLoading,
  setError,
  setCurrentAddress,
  setIsEditing,
  resetAddressForm,
  addGuestAddress,
  updateGuestAddress,
  deleteGuestAddress,
} = addressSlice.actions;

// API Actions
export const fetchAddressList = () => async (dispatch, getState) => {
  dispatch(setLoading(true));
  try {
    const { user } = getState();
    const token = localStorage.getItem("token");
    const userId = user.guestUser?._id;
    
    // If user is logged in, clear any guest addresses from localStorage
    if (token) {
      localStorage.removeItem("guestAddresses");
    }
    
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/addresses${userId ? `?userId=${userId}&isGuest=true` : ''}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      }
    );
    const data = await res.json();
    if (data.success) {
      const addresses = data.responseObject;
      // Only update if addresses have changed
      const currentAddresses = getState().address.addressList;
      if (JSON.stringify(currentAddresses) !== JSON.stringify(addresses)) {
        dispatch(setAddressList(addresses));
      }

      // First check for default address
      const defaultAddress = addresses?.find((addr) => addr.isDefault);
      if (defaultAddress && getState().address.selectedAddressId !== defaultAddress._id) {
        dispatch(setSelectedAddress(defaultAddress._id));
        return defaultAddress._id;
      }
      // If no default, use the first address
      else if (addresses?.length > 0 && !getState().address.selectedAddressId) {
        dispatch(setSelectedAddress(addresses[0]._id));
        return addresses[0]._id;
      }
    } else {
      dispatch(setError(data.message || "Failed to fetch addresses"));
    }
  } catch (error) {
    console.error("Fetch address error:", error);
    dispatch(setError(error.message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchCountries = () => async (dispatch) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}admin/filter-country-list`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    const data = await res.json();
    // console.log(data);
    dispatch(setCountryList(data.data));
  } catch (error) {
    dispatch(setError(error.message));
    // console.log("fetch country error", error);
  }
};

export const fetchStates = (countryId) => async (dispatch) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}admin/country-state-list/${countryId}`, {
      method: "GET",
      headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
        );
    const data = await res.json();
    dispatch(setStateList(data.data));
  } catch (error) {
    dispatch(setError(error.message));
    // console.log("fetch states error", error);
  }
};

export const saveAddress = (values, isEditing, currentAddressId) => async (dispatch, getState) => {
  try {
    const token = localStorage.getItem("token");
    const { guestUser } = getState().user;
    let res;
    
    // For guest users without token but with verified OTP
    if (!token && guestUser?._id) {
      res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/addresses`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...values,
          userId: guestUser._id,
          isGuest: true
        })
      });
    } else if (token) {
      // Regular logged-in user flow
      if (isEditing) {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/addresses/${currentAddressId}`, {
          method: "PUT",
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(values)
        });
      } else {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/addresses`, {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(values)
        });
      }
    } else {
      // Store address locally for completely new users
      const localAddresses = JSON.parse(localStorage.getItem('guestAddresses') || '[]');
      const newAddress = { ...values, _id: Date.now().toString() };
      localStorage.setItem('guestAddresses', JSON.stringify([...localAddresses, newAddress]));
      dispatch(setAddressList([...localAddresses, newAddress]));
      return newAddress._id;
    }

    const data = await res.json();
    if (data.success) {
      toast.success(data.message);
      dispatch(fetchAddressList());
      return data.data._id;
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    dispatch(setError(error.message));
    // console.log("address save error", error);
  }
};

export const deleteAddress = (addressId) => async (dispatch, getState) => {
  try {
    const { auth } = getState();
    if (!auth.token) {
      dispatch(deleteGuestAddress(addressId));
      toast.success("Address deleted successfully");
      return;
    }
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/addresses/${addressId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
    const data = await res.json();

    if (data.success) {
      toast.success(data.message);
      dispatch(fetchAddressList());
      dispatch(resetAddressForm());
    } else {
      toast.error(data.message);
    }
  } catch (error) {
    dispatch(setError(error.message));
    // console.log("address delete error", error);
  }
};

export default addressSlice.reducer;
